import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Tabs, Tab, FooterButton } from 'Core/components';
import CustomPropTypes from 'Core/prop-types';
import { AppModal } from '../../components/app-modals';
import { deleteByWorkTicketId, updateProcess } from '../store/slice';
import { selectChannels } from '../store/selectors';
import { showModal } from 'Core/redux/main/slice';
import { selectors as locationSelectors } from 'Core/redux/location';
import { MODAL_TYPES, ConfirmModal } from 'Feature/components/app-modals';
import { set } from 'date-fns';
const { CONFIRM_MODAL } = MODAL_TYPES;

const statusOptions = [
  "Open",
  "In Planning",
  "Pre Release",
  "In Production",
  "On Hold",
  "Machine Down",
  "Maintenance",
  "Completed"
];

const partFieldNames = [
  "partNumber",
  "boxQuantity",
  "palletQuantity",
  "plannedQuantity",
  "locationId",
  "licensePlateNumber",
  "po"
];

const priorityOptions = Array.from({ length: 10 }, (_, i) => i + 1);

const ProgramAddEdit = ({ showModal: isShowModal, modalData: initialProcessData, onCloseModal }) => {
  const [processData, setProcessData] = useState(initialProcessData);
  const [editingPart, setEditingPart] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const locations = useSelector(locationSelectors.selectLocations);
  const channels = useSelector(selectChannels);
  const dispatch = useDispatch();

  useEffect(() => {
    setProcessData(initialProcessData);
  }, [initialProcessData]);

  const handleClose = () => {
    onCloseModal();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setIsFormDirty(true);
    if (partFieldNames.some(fieldName => name.startsWith(fieldName))) {
      const index = parseInt(name.split('-')[1], 10);
      const fieldName = name.split('-')[0];
      const updatedParts = processData.processParts.map((part, i) => 
        i === index ? { ...part, [fieldName]: value } : part
      );
      setProcessData((prevData) => ({ ...prevData, processParts: updatedParts }));
    } else {
      setProcessData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const body = () => {
    return (
      <div className="">
        <div>
          <div>
            <div className="row">
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Priority</label>
                  <select
                    className="form-control"
                    name="priority"
                    value={processData.priority || ''}
                    onChange={handleChange}
                  >
                    {priorityOptions.map((priority, index) => (
                      <option key={index} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="fromDate"
                    value={processData.fromDate ? new Date(processData.fromDate).toISOString().split('T')[0] : ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Due Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="toDate"
                    value={processData.toDate ? new Date(processData.toDate).toISOString().split('T')[0] : ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Machine</label>
                  <select
                    className="form-control"
                    name="machineId"
                    value={processData.machineId || ''}
                    onChange={handleChange}
                  >
                    {channels.map((channel) => (
                      <option key={channel.uuid} value={channel.uuid}>
                        {channel.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Status</label>
                  <select
                    className="form-control"
                    name="status"
                    value={processData.status || ''}
                    onChange={handleChange}
                  >
                    {statusOptions.map((status, index) => (
                      <option key={index} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Mold Cavity Count</label>
                  <input
                    type="number"
                    className="form-control"
                    name="moldCavityCount"
                    value={processData.moldCavityCount || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Cycle Time</label>
                  <input
                    type="text"
                    className="form-control"
                    name="cycleTime"
                    value={processData.cycleTime || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Work Ticket Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="workTicketNumber"
                    value={processData.workTicketNumber || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">Origin Address</label>
                  <input
                    type="text"
                    className="form-control"
                    name="originAddress"
                    value={processData.originAddress || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-1">
                  <label className="fw-bold">OPCode</label>
                  <input
                    type="text"
                    className="form-control"
                    name="opCode"
                    value={processData.opCode || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'hidden' }}>
              {processData?.processParts?.map((part, index) => (
                <fieldset key={index} className="border p-2 m-3">
                  <legend className="w-auto">Part {index + 1}</legend>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="mb-1">
                        <label className="fw-bold">Part Number</label>
                        <input
                          type="text"
                          className="form-control"
                          name={`partNumber-${index}`}
                          value={part.partNumber || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-1">
                        <label className="fw-bold">Box Quantity</label>
                        <input
                          type="number"
                          className="form-control"
                          name={`boxQuantity-${index}`}
                          value={part.boxQuantity || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-1">
                        <label className="fw-bold">Boxes per Pallet</label>
                        <input
                          type="number"
                          className="form-control"
                          name={`palletQuantity-${index}`}
                          value={part.palletQuantity || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-1">
                        <label className="fw-bold">Planned Quantity</label>
                        <input
                          type="number"
                          className="form-control"
                          name={`plannedQuantity-${index}`}
                          value={part.plannedQuantity || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-1">
                        <label className="fw-bold">Location</label>
                        <select
                          className="form-control"
                          name={`locationId-${index}`}
                          value={part.locationId || ''}
                          onChange={handleChange}
                        >
                          <option key="0" value="0">
                            Select a Location
                          </option>
                          {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-1">
                        <label className="fw-bold">License Plate Number</label>
                        <input
                          type="text"
                          className="form-control"
                          name={`licensePlateNumber-${index}`}
                          value={part.licensePlateNumber || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="mb-1">
                        <label className="fw-bold">PO</label>
                        <input
                          type="text"
                          className="form-control"
                          name={`po-${index}`}
                          value={part.po || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </fieldset>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const valid = () => {
    if (false) {
      //   dispatch(
      //     showToast({
      //       message: 'Please fill all required fields',
      //       type: 'danger',
      //     }),
      //   );
      return false;
    }
    return true;
  };

  const footer = () => {
    const onSave = () => {
      if (valid()) {
        const payload = { ...processData };
        delete payload.materialBatchNumber;
        delete payload.MaterialBatchNumber;
        dispatch(updateProcess(payload));
      }
    };

    const onConfirmDelete = () => {
      dispatch(deleteByWorkTicketId(processData.id));
      setShowDeleteModal(false);
      handleClose();
    };

    return (
      <div className="row">
        <div className="col-lg-8">
          <button className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
            Delete Work Item
          </button>

          <ConfirmModal
            showModal={showDeleteModal}
            title="Delete Work Item"
            message="Are you sure you want to delete this work item?"
            onCloseModal={() => setShowDeleteModal(false)}
            onConfirmModal={onConfirmDelete}
          />
        </div>
        <div className="col-lg-4">
          <FooterButton
            cancelText="Cancel"
            saveText="Save Plan"
            onSave={onSave}
            onCancel={handleClose}
            disableSave={!isFormDirty}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      id="program-modal"
      show={isShowModal}
      size="xl"
      title="Plan"
      body={body}
      footer={footer}
      handleClose={handleClose}
    />
  );
};

ProgramAddEdit.propTypes = {
  showModal: PropTypes.bool,
  modalData: PropTypes.shape(CustomPropTypes.Shipment),
  onCloseModal: PropTypes.func,
};

export default AppModal(ProgramAddEdit);
